import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

import styles from './AttributeTag.module.css';

/**
 * AttributeTag — a fact about a record that is not its state.
 *
 * Handoff, "Tags & badges": three shapes, three jobs, "do not blur them".
 * StatusPill is the state of a record; AttributeTag is its category, product
 * type or perception code; CountBadge is a number. The visible difference is
 * the radius — pill vs `sm` — and it is load-bearing: an agent scanning a
 * column reads shape before they read colour.
 *
 * `tone` is a visual weight, not a meaning. There is deliberately no
 * `tone="escalated"` and no way to reach a status hue; see the note in
 * AttributeTag.module.css.
 */

export type AttributeTagTone = 'neutral' | 'outline' | 'accent';

export interface AttributeTagProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  tone?: AttributeTagTone;
  /** Decorative glyph before the label. Hidden from assistive technology. */
  icon?: ReactNode;
  /** The word. A tag with no children is a coloured rectangle. */
  children: ReactNode;
}

export const AttributeTag = forwardRef<HTMLSpanElement, AttributeTagProps>(function AttributeTag(
  { tone = 'neutral', icon, children, className, ...rest },
  ref,
) {
  return (
    <span
      {...rest}
      ref={ref}
      className={[styles['tag'], styles[tone], className].filter(Boolean).join(' ')}
      data-tone={tone}
    >
      {icon ? <span aria-hidden="true">{icon}</span> : null}
      {children}
    </span>
  );
});
